import { ThrowLiteError } from '../../lite-error.js';
import { fgInt } from '../custom-types/fg-integer.js';
import { fgMatrix3D, fgMatrix2D } from '../custom-types/fg-matrix.js';
import { getBlockDef } from '../block-registry.js';
import { FgType } from '../types.js';
import { getOpMapping, DEFAULT_VALUE_SOCKET, DEFAULT_FLOW_SOCKET } from './declaration-mapper.js';
import { parseJsonPointerTemplate, substituteJsonPointerTemplate } from './json-pointer-template.js';

const SIGNATURE_TO_FGTYPE = {
  bool: FgType.Boolean,
  int: FgType.Integer,
  float: FgType.Number,
  float2: FgType.Vector2,
  float3: FgType.Vector3,
  float4: FgType.Vector4,
  float2x2: FgType.Matrix2D,
  float3x3: FgType.Matrix3D,
  float4x4: FgType.Matrix,
  ref: FgType.Reference
};
function arrayToFgValue(arr, type) {
  const missingNumber = arr === void 0 ? NaN : 0;
  const numberAt = (index) => {
    const value = arr?.[index];
    return typeof value === "number" ? value : typeof value === "string" ? Number.parseFloat(value) : missingNumber;
  };
  switch (type) {
    case FgType.Boolean:
      return !!arr?.[0];
    case FgType.Integer:
      return fgInt(numberAt(0) || 0);
    case FgType.Number:
      return numberAt(0);
    case FgType.Reference:
    case FgType.String:
      return typeof arr?.[0] === "string" ? arr[0] : "";
    case FgType.Vector2:
      return { x: numberAt(0), y: numberAt(1) };
    case FgType.Vector3:
      return { x: numberAt(0), y: numberAt(1), z: numberAt(2) };
    case FgType.Vector4:
    case FgType.Quaternion:
      return { x: numberAt(0), y: numberAt(1), z: numberAt(2), w: numberAt(3) };
    case FgType.Matrix2D:
      return fgMatrix2D(Array.from({ length: 4 }, (_, i) => numberAt(i)));
    case FgType.Matrix3D:
      return fgMatrix3D(Array.from({ length: 9 }, (_, i) => numberAt(i)));
    case FgType.Matrix: {
      const m = new Float32Array(16);
      for (let i = 0; i < 16; i++) {
        m[i] = numberAt(i);
      }
      return m;
    }
    default:
      return arr?.[0];
  }
}
function flowOutputName(mapping, flowKeys, key) {
  if (mapping.dynamicSequence) {
    return `out_${flowKeys.indexOf(key)}`;
  }
  if (mapping.switchOutputs && key !== "default") {
    return `out_${key}`;
  }
  return mapping.flowOutputs?.[key] ?? key;
}
async function parseInteractivityGraph(json) {
  const signatures = (json.types ?? []).map((type) => type.signature);
  if (new Set(signatures).size !== signatures.length) {
    ThrowLiteError(53);
  }
  const types = signatures.map((signature) => {
    const type = SIGNATURE_TO_FGTYPE[signature];
    if (!type) {
      ThrowLiteError(54, JSON.stringify(signature));
    }
    return type;
  });
  const declarations = json.declarations ?? [];
  const nodes = json.nodes ?? [];
  const mappings = [];
  for (const node of nodes) {
    const decl = declarations[node.declaration];
    const mapping = decl ? getOpMapping(decl.op, decl.extension) : null;
    if (!decl) {
      ThrowLiteError(55, node.declaration);
    }
    mappings.push(mapping);
  }
  const variables = {};
  (json.variables ?? []).forEach((v, i) => {
    const t = types[v.type] ?? FgType.Any;
    if (!types[v.type]) {
      ThrowLiteError(56, i, v.type);
    }
    variables[String(i)] = { type: t, value: arrayToFgValue(v.value, t) };
  });
  let anonymousEventIndex = 0;
  const events = (json.events ?? []).map((event, index) => {
    const values = Object.entries(event.values ?? {}).map(([name, value]) => {
      const type = types[value.type];
      if (!type) {
        ThrowLiteError(57, index, JSON.stringify(name), value.type);
      }
      return { name, type, value: arrayToFgValue(value.value, type) };
    });
    return { id: event.id ?? `internalEvent_${anonymousEventIndex++}`, values };
  });
  const blocks = [];
  const pointers = [];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const mapping = mappings[i];
    const declaration = declarations[node.declaration];
    const config = {};
    if (!mapping) {
      config.inputs = Object.entries(declaration.inputValueSockets ?? {}).map(([name, socket]) => ({ name, type: types[socket.type] ?? FgType.Any }));
      config.outputs = Object.entries(declaration.outputValueSockets ?? {}).map(([name, socket]) => {
        const type = types[socket.type] ?? FgType.Any;
        return { name, type, defaultValue: arrayToFgValue(void 0, type) };
      });
      config.signalInputs = Object.keys(declaration.inputFlowSockets ?? { in: {} });
    }
    if (mapping?.dynamicSequence) {
      config.outputSignalCount = Object.keys(node.flows ?? {}).length;
    }
    if (mapping?.variableConfigKey) {
      const idx = node.configuration?.[mapping.variableConfigKey]?.value?.[0];
      if (typeof idx !== "number" || !json.variables?.[idx]) {
        ThrowLiteError(58, declaration.op, i, String(idx));
      }
      config.variable = String(idx);
    }
    if (mapping?.nodeConfigKey) {
      config[mapping.nodeConfigKey] = node.configuration?.[mapping.nodeConfigKey]?.value?.[0];
    }
    if (mapping?.configKeys) {
      for (const [gltfKey, liteName] of Object.entries(mapping.configKeys)) {
        const raw = node.configuration?.[gltfKey]?.value;
        if (raw !== void 0) {
          config[liteName] = raw[0];
        }
      }
    }
    if (mapping?.configArrayKeys) {
      for (const [gltfKey, liteName] of Object.entries(mapping.configArrayKeys)) {
        const raw = node.configuration?.[gltfKey]?.value;
        if (raw !== void 0) {
          config[liteName] = raw;
        }
      }
    }
    if (declaration.op === "variable/set") {
      const current = node.configuration?.variables?.value;
      const legacy = node.configuration?.variable?.value?.[0];
      const indices = current ?? (typeof legacy === "number" ? [legacy] : []);
      if (indices.length === 0 || indices.some((index) => typeof index !== "number" || !json.variables?.[index]) || new Set(indices).size !== indices.length) {
        ThrowLiteError(59, i);
      }
      const variableIndices = indices;
      for (const index of variableIndices) {
        const input = node.values?.[String(index)];
        if (!input) {
          ThrowLiteError(60, i, index);
        }
        const expectedType = types[json.variables[index].type];
        if (input.type !== void 0 && types[input.type] !== expectedType) {
          ThrowLiteError(61, i, index);
        }
      }
      config.variables = variableIndices;
      config.variableTypes = Object.fromEntries(variableIndices.map((index) => [String(index), types[json.variables[index].type] ?? FgType.Any]));
    }
    if (mapping?.pointer) {
      const template = node.configuration?.pointer?.value?.[0];
      if (!template) {
        ThrowLiteError(62, i);
      }
      let parsedTemplate;
      try {
        parsedTemplate = parseJsonPointerTemplate(template);
      } catch (error) {
        throw new Error(`KHR_interactivity: node ${i} has an invalid pointer template: ${error instanceof Error ? error.message : String(error)}`, { cause: error });
      }
      for (const parameter of parsedTemplate.parameters) {
        const input = node.values?.[parameter.id];
        if (!input) {
          ThrowLiteError(63, i, JSON.stringify(parameter.id));
        }
        const actualType = input.type === void 0 ? void 0 : types[input.type];
        const expectedType = parameter.kind === "integer" ? FgType.Integer : FgType.Reference;
        const legacyNumericReference = parameter.kind === "reference" && (actualType === FgType.Integer || actualType === FgType.Number);
        if (actualType !== void 0 && actualType !== expectedType && !legacyNumericReference) {
          ThrowLiteError(64, i, JSON.stringify(parameter.id));
        }
      }
      const resolved = substituteJsonPointerTemplate(parsedTemplate, (parameter) => {
        const input = node.values?.[parameter.id];
        return input?.node === void 0 ? input?.value?.[0] : void 0;
      });
      const segments = parsedTemplate.parameters.map((parameter) => parameter.id);
      config.pointerParameterIds = segments;
      const reserved = declaration.op === "pointer/set" ? ["value"] : declaration.op === "pointer/interpolate" ? ["value", "duration", "p1", "p2"] : [];
      if (segments.some((parameter) => reserved.includes(parameter))) {
        ThrowLiteError(65, declaration.op, i);
      }
      if (resolved === null) {
        config.pointerTemplate = template;
        config.pointerSegments = segments;
      } else {
        config.accessor = resolved;
        if (!pointers.includes(resolved)) {
          pointers.push(resolved);
        }
      }
      const configuredType = node.configuration?.type?.value?.[0];
      const inferredType = declaration.op === "pointer/set" ? node.values?.value?.type : void 0;
      const typeIndex = typeof configuredType === "number" ? configuredType : inferredType;
      if (typeIndex !== void 0) {
        if (!types[typeIndex]) {
          ThrowLiteError(66, declaration.op, i, String(typeIndex));
        }
        config.type = types[typeIndex];
      }
    }
    if (declaration.op === "event/send" || declaration.op === "event/receive") {
      const eventIndex = node.configuration?.event?.value?.[0];
      if (typeof eventIndex !== "number" || !events[eventIndex]) {
        ThrowLiteError(67, i, String(eventIndex));
      }
      const event = events[eventIndex];
      config.eventId = event.id;
      config.valueNames = event.values.map((value) => value.name);
      config.valueTypes = event.values.map((value) => value.type);
      config.valueDefaults = Object.fromEntries(event.values.map((value) => [value.name, value.value]));
      config.dispatchEventsSynchronously = false;
    }
    if (declaration.op === "variable/interpolate" || declaration.op === "pointer/interpolate") {
      for (const socket of ["value", "duration", "p1", "p2"]) {
        if (!node.values?.[socket]) {
          ThrowLiteError(68, declaration.op, i, socket);
        }
      }
      let interpolationType;
      if (declaration.op === "variable/interpolate") {
        const variableIndex = node.configuration?.variable?.value?.[0];
        if (typeof variableIndex !== "number" || !json.variables?.[variableIndex]) {
          ThrowLiteError(69, i, String(variableIndex));
        }
        interpolationType = types[json.variables[variableIndex].type];
        config.variable = String(variableIndex);
        if (config.useSlerp === true && interpolationType !== FgType.Vector4) {
          ThrowLiteError(70, i);
        }
      } else {
        const typeIndex = node.configuration?.type?.value?.[0];
        if (typeof typeIndex !== "number" || !types[typeIndex]) {
          ThrowLiteError(71, i, String(typeIndex));
        }
        interpolationType = types[typeIndex];
      }
      if (interpolationType === FgType.Boolean || interpolationType === FgType.Integer) {
        ThrowLiteError(72, declaration.op, i, interpolationType);
      }
      config.type = interpolationType;
    }
    const blockType = mapping?.block ?? "NoOp";
    const def = await getBlockDef(blockType)();
    const shape = def.build(config);
    blocks.push({
      id: `node_${i}`,
      type: blockType,
      config,
      dataIn: [...shape.dataIn ?? []],
      dataOut: shape.dataOut ?? [],
      signalIn: shape.signalIn ?? [],
      signalOut: (shape.signalOut ?? []).map((s) => ({ name: s.name, targets: [] })),
      event: shape.event
    });
  }
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const mapping = mappings[i];
    const block = blocks[i];
    for (const [gltfKey, entry] of Object.entries(node.values ?? {})) {
      const inputName = mapping?.valueInputs?.[gltfKey] ?? gltfKey;
      const socket = block.dataIn.find((d) => d.name === inputName);
      if (!socket) {
        continue;
      }
      if (entry.node !== void 0) {
        if (!blocks[entry.node]) {
          ThrowLiteError(73, i, entry.node);
        }
        const producer = mappings[entry.node];
        const gltfSocket = entry.socket ?? DEFAULT_VALUE_SOCKET;
        socket.source = {
          blockId: `node_${entry.node}`,
          socket: producer?.outputValues?.[gltfSocket] ?? gltfSocket,
          scale: mapping?.connectedValueScale?.[gltfKey]
        };
      } else {
        const raw = entry.value;
        const transform = mapping?.valueTransform?.[gltfKey];
        const arr = transform && raw ? transform(raw) : raw;
        socket.defaultValue = arrayToFgValue(arr, types[entry.type ?? -1] ?? socket.type);
      }
    }
    const flowKeys = Object.keys(node.flows ?? {});
    for (const key of flowKeys) {
      const flow = node.flows[key];
      if (!blocks[flow.node]) {
        ThrowLiteError(74, i, JSON.stringify(key), flow.node);
      }
      if (!mapping) {
        continue;
      }
      const outName = flowOutputName(mapping, flowKeys, key);
      const out = block.signalOut.find((s) => s.name === outName);
      if (!out) {
        continue;
      }
      out.targets.push({ blockId: `node_${flow.node}`, socket: flow.socket ?? DEFAULT_FLOW_SOCKET });
    }
  }
  const byId = {};
  blocks.forEach((b, i) => byId[b.id] = i);
  return { graph: { blocks, byId, variables }, pointers };
}

export { parseInteractivityGraph };
//# sourceMappingURL=interactivity-parser.js.map
